// Balizas de novedades (video aprobado "Señala lo nuevo sin estorbar"). Un punto que late en la esquina de cada
// novedad que esta en pantalla; al tocarlo, una nota corta al lado. Una pildora "x de n novedades vistas" abre la
// lista. Nada tapa la pantalla: no hay velo ni modal y se puede seguir trabajando. Lo visto se recuerda.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { Icon } from '../lib/icons';
import { cuantasVistas, guardarVistas, leerVistas, NOVEDADES, pendientes, type Novedad } from '../lib/novedades';
import { phone } from '../phone/store';

// Punto de la baliza (esquina superior derecha) y el rectangulo del elemento, para ubicar la nota.
type Punto = { x: number; y: number; top: number; bottom: number; left: number; right: number };

// Rectangulo visible del elemento (recortado por sus contenedores con scroll y por la ventana), o null si no se ve.
function puntoVisible(el: Element): Punto | null {
  if (el.closest('[inert],[aria-hidden="true"],[hidden]')) return null;
  const ver = (el as Element & { checkVisibility?: (o: object) => boolean }).checkVisibility;
  if (ver && !ver.call(el, { opacityProperty: true, visibilityProperty: true })) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return null;
  let top = 0;
  let left = 0;
  let bottom = window.innerHeight;
  let right = window.innerWidth;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const cs = getComputedStyle(p);
    if (cs.opacity === '0') return null;
    if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
      const pr = p.getBoundingClientRect();
      top = Math.max(top, pr.top);
      left = Math.max(left, pr.left);
      bottom = Math.min(bottom, pr.bottom);
      right = Math.min(right, pr.right);
    }
  }
  // La baliza va en la esquina superior derecha; si esa esquina queda fuera de lo visible, no se muestra.
  const x = Math.round(r.right - 3);
  const y = Math.round(r.top + 3);
  if (x < left || x > right || y < top || y > bottom) return null;
  return { x, y, top: Math.round(r.top), bottom: Math.round(r.bottom), left: Math.round(r.left), right: Math.round(r.right) };
}

const buscar = (n: Novedad) => {
  for (const el of document.querySelectorAll(n.selector)) {
    const p = puntoVisible(el);
    if (p) return p;
  }
  return null;
};

const igual = (a: Record<string, Punto>, b: Record<string, Punto>) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => b[k] && a[k].x === b[k].x && a[k].y === b[k].y && a[k].bottom === b[k].bottom && a[k].left === b[k].left);
};

export function Balizas() {
  const loc = useLocation();
  const nav = useNavigate();
  const [vistas, setVistas] = useState<string[]>(leerVistas);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [lista, setLista] = useState(false);
  const [puntos, setPuntos] = useState<Record<string, Punto>>({});
  const [recienCompleta, setRecienCompleta] = useState(false);
  const pend = useMemo(() => pendientes(vistas), [vistas]);
  const vistasN = cuantasVistas(vistas);
  const total = NOVEDADES.length;
  const claveP = pend.map((n) => n.id).join(',');

  const marcar = (ids: string[]) => {
    const nuevas = [...new Set([...vistas, ...ids])];
    setVistas(nuevas);
    guardarVistas(nuevas);
    if (cuantasVistas(nuevas) === total && vistasN < total) setRecienCompleta(true);
  };

  // Al completar, la pildora dice "Todo descubierto" un momento y se va.
  useEffect(() => {
    if (!recienCompleta) return;
    const t = setTimeout(() => setRecienCompleta(false), 3500);
    return () => clearTimeout(t);
  }, [recienCompleta]);

  // Posiciones: se recalculan con scroll, cambio de tamano, de pantalla o del DOM (p. ej. al abrir la isla).
  const medir = useCallback(() => {
    const nuevos: Record<string, Punto> = {};
    const buscadas = claveP.split(',');
    for (const n of NOVEDADES) {
      if (!buscadas.includes(n.id) && n.id !== abierta) continue;
      const p = buscar(n);
      if (p) nuevos[n.id] = p;
    }
    setPuntos((v) => (igual(v, nuevos) ? v : nuevos));
  }, [claveP, abierta]);

  useEffect(() => {
    if (!claveP && !abierta) {
      setPuntos({});
      return;
    }
    let raf = 0;
    const pedir = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0;
          medir();
        });
    };
    pedir();
    window.addEventListener('scroll', pedir, true);
    window.addEventListener('resize', pedir);
    const mo = new MutationObserver((rs) => {
      // Los cambios de las propias balizas no cuentan (evita medir sin fin).
      if (rs.every((r) => (r.target as Element).closest?.('[data-baliza-ui]'))) return;
      pedir();
    });
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'inert', 'hidden', 'open', 'data-mode', 'aria-expanded'] });
    // Animaciones hechas solo con CSS: se mide cuando terminan (antes: cada 1,2 s, siempre, aunque nada cambiara).
    document.addEventListener('transitionend', pedir, true);
    document.addEventListener('animationend', pedir, true);
    // Al cambiar de pantalla, otra vez cuando termina de aparecer (las pantallas que se bajan aparte llegan despues).
    const t = setTimeout(pedir, 400);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', pedir, true);
      window.removeEventListener('resize', pedir);
      document.removeEventListener('transitionend', pedir, true);
      document.removeEventListener('animationend', pedir, true);
      mo.disconnect();
      clearTimeout(t);
    };
  }, [claveP, abierta, medir, loc.pathname]);

  // Clic fuera o Escape cierran la nota y la lista (sin bloquear nada).
  useEffect(() => {
    if (!abierta && !lista) return;
    const fuera = (e: PointerEvent) => {
      if ((e.target as Element).closest?.('[data-baliza-ui]')) return;
      setAbierta(null);
      setLista(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setAbierta(null);
      setLista(false);
    };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierta, lista]);

  const abrirDesdeLista = (n: Novedad) => {
    setLista(false);
    setAbierta(n.id);
    if (buscar(n)) return;
    if (n.enTelefono) phone.expand('ajustes');
    else if (n.ruta && loc.pathname !== n.ruta) nav(n.ruta);
  };

  const pildora = pend.length > 0 || recienCompleta || lista;
  const nota = abierta ? NOVEDADES.find((n) => n.id === abierta) : null;
  const anclaNota = abierta ? puntos[abierta] : undefined;

  return createPortal(
    <div className="balizas" data-baliza-ui>
      {pend.map((n) =>
        puntos[n.id] ? (
          <button
            key={n.id}
            type="button"
            className={`baliza ${abierta === n.id ? 'activa' : ''}`}
            style={{ left: puntos[n.id].x, top: puntos[n.id].y }}
            aria-label={`Novedad: ${n.titulo}`}
            aria-expanded={abierta === n.id}
            onClick={() => (setLista(false), setAbierta((a) => (a === n.id ? null : n.id)))}
          />
        ) : null,
      )}
      {nota ? (
        <NotaNovedad
          key={nota.id}
          n={nota}
          ancla={anclaNota}
          vista={vistas.includes(nota.id)}
          onEntendido={() => (marcar([nota.id]), setAbierta(null))}
          onTodas={() => (marcar(NOVEDADES.map((x) => x.id)), setAbierta(null))}
          onCerrar={() => setAbierta(null)}
        />
      ) : null}
      {pildora ? (
        <div className={`ba-pildora ${pend.length === 0 ? 'completa' : ''}`}>
          {lista ? (
            <ul className="ba-lista" aria-label="Novedades">
              {NOVEDADES.map((n) => (
                <li key={n.id}>
                  <button type="button" onClick={() => abrirDesdeLista(n)}>
                    <span className={`ba-check ${vistas.includes(n.id) ? 'on' : ''}`} aria-label={vistas.includes(n.id) ? 'Vista' : 'Sin ver'}>
                      <Icon name="check" size={11} />
                    </span>
                    <span className="ba-txt">
                      <b>{n.titulo}</b>
                      <small>{n.texto}</small>
                    </span>
                  </button>
                </li>
              ))}
              <li className="ba-pie">
                {pend.length ? (
                  <button type="button" className="ba-link" onClick={() => (marcar(NOVEDADES.map((x) => x.id)), setLista(false))}>
                    Ya las vi todas
                  </button>
                ) : null}
                {vistasN ? (
                  <button type="button" className="ba-link" onClick={() => (setVistas([]), guardarVistas([]), setLista(false))}>
                    Volver a mostrar todas
                  </button>
                ) : null}
              </li>
            </ul>
          ) : null}
          <button type="button" className="ba-pil" onClick={() => (setAbierta(null), setLista((v) => !v))} aria-expanded={lista}>
            <span className="ba-anillo" style={{ ['--p' as string]: (vistasN / total) * 100 }} aria-hidden />
            {pend.length === 0 ? 'Todo descubierto' : `${vistasN} de ${total} novedades vistas`}
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

const ANCHO_NOTA = 264;

function NotaNovedad({ n, ancla, vista, onEntendido, onTodas, onCerrar }: { n: Novedad; ancla?: Punto; vista: boolean; onEntendido: () => void; onTodas: () => void; onCerrar: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [alto, setAlto] = useState(140);
  useLayoutEffect(() => {
    if (ref.current) setAlto(ref.current.offsetHeight);
  }, [n.id]);
  // Al lado del elemento si cabe (no tapa lo que senala); si no, debajo o encima. Siempre dentro de la ventana.
  // Sin ancla (el elemento no esta en pantalla), sobre la pildora.
  let estilo: CSSProperties | undefined;
  if (ancla) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dentro = (v: number, max: number) => Math.min(Math.max(8, v), max - 8);
    if (ancla.right + 14 + ANCHO_NOTA <= vw - 8) estilo = { left: ancla.right + 14, top: dentro(ancla.top - 6, vh - alto) };
    else {
      const left = dentro(ancla.left, vw - ANCHO_NOTA);
      estilo = ancla.bottom + 10 + alto < vh - 8 ? { left, top: ancla.bottom + 10 } : { left, top: Math.max(8, ancla.top - 10 - alto) };
    }
  }
  return (
    <div ref={ref} className={`ba-nota ${ancla ? '' : 'sin-ancla'}`} style={estilo} role="dialog" aria-modal="false" aria-label={n.titulo}>
      <p className="ba-cap">
        <span aria-hidden /> Novedad
      </p>
      <b>{n.titulo}</b>
      <p>{n.texto}</p>
      {!ancla ? <p className="ba-donde">{n.ruta || n.enTelefono ? 'Te llevamos a donde está…' : 'Aparecerá marcada cuando abras esa pantalla.'}</p> : null}
      <div className="ba-acc">
        <button type="button" className="btn sm ghost" onClick={onTodas}>
          Ya las vi todas
        </button>
        <button type="button" className="btn sm primary" onClick={vista ? onCerrar : onEntendido}>
          Entendido
        </button>
      </div>
    </div>
  );
}
